import { academyRequest } from "./academyApi";
import { useAcademicRead, readAcademicCurricula, readAcademicProgramVersions } from "./useAcademicCatalogue";
import type {
  AcademicContent,
  AcademicLesson,
  AcademicProgramPage,
  AcademicProgramSummary,
  CurriculumSummary,
  AcademicQuality,
  AcademicVersionEvidence,
} from "./generated/academic-catalogue";

export const lessonFormats = {
  live_self_paced: "Live + self-paced",
  live_workshop: "Live workshop",
  self_paced: "Self-paced",
  clinic: "Clinic",
} as const;
export const academicLessonRoute = (programId: string, curriculumId: string, lessonId: string) =>
  `lesson-${programId}_${curriculumId}_${lessonId}`;
export function academicProgramIdentity(page: string) {
  const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
  const match = new RegExp(`^program-(${uuid})(?:_(${uuid}))?$`, "i").exec(page);
  return match ? { programId: match[1], curriculumId: match[2] } : undefined;
}
export function academicLessonIdentity(page: string) {
  const match = /^lesson-([0-9a-f-]{36})_([0-9a-f-]{36})_([0-9a-f-]{36})$/i.exec(page);
  return match ? { programId: match[1], curriculumId: match[2], lessonId: match[3] } : undefined;
}
export const readAcademic = <T>(path: string, signal: AbortSignal) =>
  academyRequest<T>(`academic-catalogue/${path}`, undefined, "GET", true, undefined, signal);
export async function readTeachingPrograms(signal: AbortSignal) {
  const items: AcademicProgramSummary[] = [],
    seen = new Set<string>();
  let cursor: string | null = null;
  do {
    const page: AcademicProgramPage = await readAcademic(
      `programs${cursor ? `?after=${encodeURIComponent(cursor)}` : ""}`,
      signal,
    );
    items.push(...page.items);
    cursor = page.nextCursor;
    if (cursor && seen.has(cursor)) throw new Error("Assigned Program pagination did not advance.");
    if (cursor) seen.add(cursor);
  } while (cursor);
  return items;
}
export const useTeachingPrograms = (enabled = true) =>
  useAcademicRead("assigned-teaching-programs", enabled, readTeachingPrograms);
export const useTeachingProgramVersions = (programId: string) =>
  useAcademicRead(`programs/${programId}/version-history`, !!programId, signal =>
    readAcademicProgramVersions(programId, signal),
  );
export const academicCurriculumStatus = (version: Pick<CurriculumSummary, "state" | "publicationId">) =>
  version.publicationId
    ? "Published"
    : {
        draft: "Draft",
        in_review: "Review",
        approved: "Approved",
        changes_requested: "Changes requested",
        rejected: "Rejected",
      }[version.state];
export function useTeachingVersionRecords(mode: "quality" | "evidence", enabled: boolean) {
  return useAcademicRead(`assigned-teaching-${mode}`, enabled, async signal => {
    const programs = await readTeachingPrograms(signal);
    const records: {
      program: AcademicProgramSummary;
      curriculum: CurriculumSummary;
      quality?: AcademicQuality;
      evidence?: AcademicVersionEvidence;
    }[] = [];
    for (const program of programs) {
      const versions = await readAcademicCurricula("program", program.id, signal, mode);
      records.push(
        ...versions.map(curriculum => ({
          program,
          curriculum,
          quality: curriculum.quality,
          evidence: curriculum.evidence,
        })),
      );
    }
    return records;
  });
}
export const useTeachingCurricula = (programId: string) =>
  useAcademicRead(`program/${programId}/curricula`, !!programId, signal =>
    readAcademicCurricula("program", programId, signal),
  );
export const useTeachingContent = (programId: string, versionId: string) =>
  useAcademicRead(`program/${programId}/curricula/${versionId}`, !!programId && !!versionId, signal =>
    readAcademic<AcademicContent>(`program/${programId}/curricula/${versionId}`, signal),
  );
export type TeachingLesson = {
  id: string;
  title: string;
  module: string;
  program: string;
  format: string;
  status: string;
  duration: string;
  accessibility: null;
  engagement: null;
  version: string;
  academic: { programId: string; curriculum: AcademicContent; lesson: AcademicLesson };
};
export function projectTeachingLesson(
  program: AcademicProgramSummary,
  content: AcademicContent,
  module: AcademicContent["modules"][number],
  lesson: AcademicLesson,
  published: boolean,
): TeachingLesson {
  return {
    id: academicLessonRoute(program.id, content.id, lesson.id),
    title: lesson.title,
    module: module.title,
    program: program.name,
    format: lesson.deliveryFormat ? lessonFormats[lesson.deliveryFormat] : "Not specified",
    status: academicCurriculumStatus({ state: content.state, publicationId: published ? content.id : null }),
    duration: "Not configured",
    accessibility: null,
    engagement: null,
    version: content.versionLabel,
    academic: { programId: program.id, curriculum: content, lesson },
  };
}
export async function readTeachingLessons(programs: AcademicProgramSummary[], signal: AbortSignal) {
  const lessons: TeachingLesson[] = [];
  for (const program of programs) {
    const versions = await readAcademicCurricula("program", program.id, signal, "content");
    versions.forEach(version => {
      const content = version.content!;
      content.modules.forEach(module =>
        module.lessons.forEach(lesson =>
          lessons.push(projectTeachingLesson(program, content, module, lesson, !!version.publicationId)),
        ),
      );
    });
  }
  return lessons;
}
export function useTeachingLessons(enabled: boolean) {
  return useAcademicRead("assigned-teaching-lessons", enabled, async signal =>
    readTeachingLessons(await readTeachingPrograms(signal), signal),
  );
}
export function useTeachingSearch(enabled: boolean) {
  return useAcademicRead("assigned-teaching-search", enabled, async signal => {
    const programs = await readTeachingPrograms(signal),
      lessons = await readTeachingLessons(programs, signal);
    return [
      ...programs.map(program => ({
        id: `program-${program.id}`,
        page: `program-${program.id}`,
        area: "teaching" as const,
        icon: "book",
        title: program.name,
        detail: program.code,
        label: "Assigned Program",
      })),
      ...lessons.map(lesson => ({
        id: lesson.id,
        page: lesson.id,
        area: "teaching" as const,
        icon: "file",
        title: lesson.title,
        detail: `${lesson.program} · ${lesson.module} · ${lesson.version} · ${lesson.status}`,
        label: "Lesson",
      })),
    ];
  });
}
